import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTodayHoroscope } from "../utils/horo";
import { translateText } from "../utils/translateText";
import "../styles/DashboardBar.css";

const cap = (s) => (!s || s === "—" ? "—" : s.charAt(0).toUpperCase() + s.slice(1));

// Kept for the weather card when that dashboard section is enabled again.
// eslint-disable-next-line no-unused-vars
function wmoToEmoji(wmo) {
  const n = Number(wmo);
  if ([0].includes(n)) return { emoji: "☀️", label: "Clear" };
  if ([1].includes(n)) return { emoji: "🌤️", label: "Mainly clear" };
  if ([2].includes(n)) return { emoji: "⛅", label: "Partly cloudy" };
  if ([3].includes(n)) return { emoji: "☁️", label: "Overcast" };
  if ([45, 48].includes(n)) return { emoji: "🌫️", label: "Fog" };
  if ([51, 53, 55].includes(n)) return { emoji: "🌦️", label: "Drizzle" };
  if ([56, 57].includes(n)) return { emoji: "🌧️", label: "Freezing drizzle" };
  if ([61, 63, 65].includes(n)) return { emoji: "🌧️", label: "Rain" };
  if ([66, 67].includes(n)) return { emoji: "🌧️❄️", label: "Freezing rain" };
  if ([71, 73, 75].includes(n)) return { emoji: "🌨️", label: "Snow" };
  if ([77].includes(n)) return { emoji: "❄️", label: "Snow grains" };
  if ([80, 81, 82].includes(n)) return { emoji: "🌧️", label: "Rain showers" };
  if ([85, 86].includes(n)) return { emoji: "🌨️", label: "Snow showers" };
  if ([95].includes(n)) return { emoji: "⛈️", label: "Thunderstorm" };
  if ([96, 99].includes(n)) return { emoji: "⛈️", label: "Thunderstorm with hail" };
  return { emoji: "🌡️", label: "Weather" };
}

function computeCategory(bmi) {
  if (bmi == null) return "—";
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "normal";
  if (bmi < 30) return "overweight";
  return "obese";
}

function readFromLocal() {
  const name = localStorage.getItem("userName") || "";
  const email = localStorage.getItem("userEmail") || "";
  const heightCm = parseFloat(localStorage.getItem("userHeight") || "") || null;
  const weightKg = parseFloat(localStorage.getItem("userWeight") || "") || null;

  let bmi = null;
  if (heightCm && weightKg) {
    bmi = +(weightKg / Math.pow(heightCm / 100, 2)).toFixed(1);
    localStorage.setItem("lastBMI", String(bmi));
  } else {
    const last = parseFloat(localStorage.getItem("lastBMI"));
    if (!Number.isNaN(last)) bmi = last;
  }
  return { greetingName: name || "", category: computeCategory(bmi), bmi, heightCm, weightKg, email };
}

export default function DashboardBar() {
  const { t, i18n } = useTranslation();
  const [dash, setDash] = useState(null);
  const [translatedCategory, setTranslatedCategory] = useState("—");

  const [zodiac, setZodiac] = useState(localStorage.getItem("horoscope") || localStorage.getItem("zodiac") || "");
  const [horo, setHoro] = useState(null);
  const [horoLoading, setHoroLoading] = useState(false);
  const [horoErr, setHoroErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      if (cancelled) return;
      const newDash = readFromLocal();
      setDash(newDash);
      const latestZodiac = localStorage.getItem("horoscope") || localStorage.getItem("zodiac") || "";
      setZodiac(latestZodiac);
    };
    refresh();
    window.addEventListener("profile-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("profile-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    const translateCategory = async () => {
      if (dash?.category && dash.category !== "—") {
        const translated = await translateText(dash.category, i18n.language || "en");
        setTranslatedCategory(translated);
      } else {
        setTranslatedCategory("—");
      }
    };
    translateCategory();
  }, [dash?.category, i18n.language]);

  useEffect(() => {
    let on = true;
    (async () => {
      if (!zodiac) { setHoro(null); setHoroErr(""); return; }
      try {
        setHoroLoading(true);
        setHoroErr("");
        const data = await getTodayHoroscope(zodiac.toLowerCase(), i18n.language || "en");
        if (on) setHoro(data || null);
      } catch {
        if (on) setHoroErr(t("could_not_load_horoscope"));
      } finally {
        if (on) setHoroLoading(false);
      }
    })();
    return () => { on = false; };
  }, [zodiac, i18n.language, t]);

  if (!dash) return null;

  return (
    <div className="home-card hero-card db-card">
      <div className="db-greeting">
        {t("hi_user_category", {
          name: dash.greetingName || t("there"),
          category: translatedCategory,
        })}
      </div>

      <div className="db-stats-row">
        <div className="db-stat">
          <div className="db-stat-label">{t("BMI")}</div>
          <div className="db-stat-value">{dash.bmi ?? "—"}</div>
        </div>
        <div className="db-stat">
          <div className="db-stat-label">{t("Height")}</div>
          <div className="db-stat-value">
            {dash.heightCm ?? "—"} <span className="db-unit">{t("cm")}</span>
          </div>
        </div>
        <div className="db-stat">
          <div className="db-stat-label">{t("Weight")}</div>
          <div className="db-stat-value">
            {dash.weightKg ?? "—"} <span className="db-unit">{t("kg")}</span>
          </div>
        </div>
      </div>

      {zodiac && (
        <div className="db-horo" style={{ marginTop: 12 }}>
          <div className="db-horo-title">🔮 {t("todays_luck")} — {cap(zodiac)}</div>
          {horoLoading && <div className="db-horo-line muted">{t("loading_stars")}</div>}
          {!horoLoading && horoErr && <div className="db-horo-line warn">{horoErr}</div>}
          {!horoLoading && horo && (
            <>
              {horo.description && (
                <div className="db-horo-line" style={{ marginBottom: 6 }}>
                  {horo.description}
                </div>
              )}
              <div className="db-horo-badges" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {horo.mood && <span className="pill">😄 {horo.mood}</span>}
                {horo.color && <span className="pill">🎨 {horo.color}</span>}
                {horo.lucky_number && <span className="pill">#️⃣ {horo.lucky_number}</span>}
                {horo.compatibility && <span className="pill">🤝 {horo.compatibility}</span>}
              </div>
              <small className="muted" style={{ display: "block", marginTop: 6 }}>
                {t("entertainment_notice")}
              </small>
            </>
          )}
        </div>
      )}
    </div>
  );
}
